import { useQuery } from "@tanstack/react-query";
import { getEngineApi } from "@/lib/api";
import { useResourceScope } from "@/lib/resource-context";
import {
  transformHistoricalLogs,
  type UnifiedLogEntry,
} from "./log-transformers";

type HistoricalLogApiEntry = {
  timestamp: string;
  line: string;
};

type HistoricalLogsParams = {
  serverId: string;
  startTimeUtc?: string;
  endTimeUtc?: string;
  enabled?: boolean;
};

const fetchManagedServerLogsHistory = async (
  organizationId: string,
  projectId: string,
  serverId: string,
  startTimeUtc?: string,
  endTimeUtc?: string
): Promise<UnifiedLogEntry[]> => {
  const api = getEngineApi({ organizationId, projectId });
  const { data } = await api.get<HistoricalLogApiEntry[]>(
    `/deployments/${serverId}/logs`,
    {
      params: {
        start_time_utc: startTimeUtc,
        end_time_utc: endTimeUtc,
      },
    }
  );
  return transformHistoricalLogs(data);
};

export const useGetManagedServerLogsHistory = ({
  serverId,
  startTimeUtc,
  endTimeUtc,
  enabled = true,
}: HistoricalLogsParams) => {
  const { organizationId, projectId } = useResourceScope();

  return useQuery({
    queryKey: [
      "managed-server-logs-history",
      organizationId,
      projectId,
      serverId,
      startTimeUtc,
      endTimeUtc,
    ],
    queryFn: () =>
      fetchManagedServerLogsHistory(
        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
        organizationId as string,
        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
        projectId as string,
        serverId,
        startTimeUtc,
        endTimeUtc
      ),
    enabled: enabled && Boolean(organizationId && projectId),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });
};
